import { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';

import { PcseEyeRx } from '../../@types/pcse';
import {
  DirectTillResult,
  HandoverService,
} from '../../api/handover.service';
import { getApiErrorMessage } from '../../utils/util';
import NhsVoucherPicker, {
  NhsVoucherSelection,
} from '../dispense/NhsVoucherPicker';

const handoverService = new HandoverService();

const money = (n: number) => `£${(Number(n) || 0).toFixed(2)}`;

interface DirectTillDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  handoverId: number;
  /** Exam type from the handover — pre-fills what is being charged for. */
  examType?: string;
  prescription?: { right?: PcseEyeRx; left?: PcseEyeRx; found?: boolean };
  /** Called with the created till so the caller can navigate to it. */
  onCreated?: (result: DirectTillResult) => void;
}

/**
 * Take payment straight off the Handover screen.
 *
 * For an NHS voucher supply or a chargeable exam, the patient is standing at
 * the desk the moment the handover is completed. Sending them round through the
 * Dispense screen to open a till line is friction that loses payments, so this
 * prices the sale and opens the till in one step.
 *
 * The amount entered is GROSS. Any NHS voucher is valued by the server and
 * deducted, so the till only ever asks the patient for their own contribution.
 */
export default function DirectTillDialog({
  open,
  onClose,
  patientId,
  handoverId,
  examType,
  prescription,
  onCreated,
}: DirectTillDialogProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState(examType ?? '');
  const [nhsVoucher, setNhsVoucher] = useState<NhsVoucherSelection | null>(null);
  const [net, setNet] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<DirectTillResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setError(null);
    setDescription(examType ?? '');
  }, [open, examType]);

  const gross = Number(amount);
  const grossValid = Number.isFinite(gross) && gross >= 0 && amount !== '';

  const handleSubmit = async () => {
    if (!grossValid) {
      setError('Enter the amount being charged.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await handoverService.createDirectTill(patientId, handoverId, {
        amount: gross,
        description: description.trim() || undefined,
        nhsVoucher: nhsVoucher?.type
          ? {
              type: nhsVoucher.type,
              supplements: nhsVoucher.supplements,
              lensesAffected: nhsVoucher.lensesAffected,
            }
          : undefined,
      });
      setResult(res.data);
      enqueueSnackbar(
        `Till raised — ${money(res.data.netAmount)} due from the patient.`,
        { variant: 'success' }
      );
      onCreated?.(res.data);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not raise the till.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Direct till</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {result ? (
          <Alert severity="success">
            <AlertTitle>Till ready</AlertTitle>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
              <Chip variant="outlined" label={`Charged ${money(result.grossAmount)}`} />
              {result.nhsVoucherValue > 0 && (
                <Chip color="info" label={`NHS voucher ${money(result.nhsVoucherValue)}`} />
              )}
              <Chip color="primary" label={`Patient pays ${money(result.netAmount)}`} />
            </Stack>
            Dispense #{result.dispenseId}
            {result.tillId ? ` · till #${result.tillId}` : ''}
            {result.voucherBand && (
              <>
                {' '}
                · band {result.voucherBand}, valued {result.voucherValuedOn}. The
                voucher is recovered from PCSE on a GOS3 claim raised against
                this dispense.
              </>
            )}
          </Alert>
        ) : (
          <Stack spacing={3}>
            <Alert severity="info">
              Charge for this visit without leaving the handover. Enter the full
              amount — any NHS voucher is deducted for you, and the till asks the
              patient only for the balance.
            </Alert>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="number"
                label="Amount charged (gross)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">£</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: '0.01' }}
              />
              <TextField
                fullWidth
                label="What for"
                placeholder="NHS Exam"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Stack>

            <Divider />

            <NhsVoucherPicker
              chargedAmount={grossValid ? gross : 0}
              value={nhsVoucher}
              onChange={setNhsVoucher}
              prescription={prescription}
              onNetChange={setNet}
            />

            {!nhsVoucher && grossValid && (
              <Box>
                <Typography variant="subtitle2">
                  Patient pays {money(gross)}
                </Typography>
              </Box>
            )}
            {nhsVoucher?.type && net != null && (
              <Box>
                <Typography variant="subtitle2">
                  Till will take {money(net)}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <LoadingButton
            variant="contained"
            loading={submitting}
            disabled={!grossValid}
            onClick={handleSubmit}
          >
            Create till
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
